import { tailwindVariants } from '@air/utils-tailwind';

/**
 * Shared outer chrome — composer and consent dock must match so the dock overlays the input exactly.
 * Prototype `AGENT_COMPOSER_SLOT_RADIUS_CLASS` (`rounded-sm`, 4px / `rounded-4`) — concentric with the
 * chat body inset so composer and approval corner arcs register.
 * `border` (not `ring-1`) so the edge survives the composer slot's `overflow-hidden` while the consent
 * dock is open; an outset ring's top stroke is clipped there.
 */
export const workspaceAgentComposerSlotShell = tailwindVariants({
  base: 'flex w-full flex-col overflow-hidden rounded-4 border ads:border-input border-gray-5 ads:bg-raised bg-gray-2',
});

/**
 * Prototype uses an **outset** focus ring so focus traces the full card — not an inset ring that the
 * scope header would cover at the top edge.
 */
export const workspaceAgentComposerShell = tailwindVariants({
  base: [
    workspaceAgentComposerSlotShell(),
    'relative transition-shadow ads:focus-within:ring-focus focus-within:ring-2 focus-within:ring-altitude-9',
    // Wizard lock uses aria-disabled, not inert. A leftover focused child (Actions) must not keep the ring.
    'aria-disabled:focus-within:ring-0',
  ],
});

/**
 * Full-bleed hit target behind composer chrome — dead zones pass `pointer-events` through the
 * foreground so this layer can focus the editor.
 */
export const workspaceAgentComposerFocusCapture = tailwindVariants({
  base: 'absolute inset-0 z-0 cursor-text',
});

/** Scope bar, editor, and footer controls opt back into hits via `workspaceAgentComposerPointerSurface`. */
export const workspaceAgentComposerForeground = tailwindVariants({
  base: 'pointer-events-none relative z-1 flex min-h-0 flex-1 flex-col',
});

export const workspaceAgentComposerPointerSurface = tailwindVariants({
  base: 'pointer-events-auto',
});

/** Generic consent dock approve CTA when the caller does not supply a tool-specific label. */
export const WORKSPACE_AGENT_CONSENT_DOCK_DEFAULT_APPROVE_LABEL = 'Approve';

/**
 * Consent dock shell — a sheet spanning the panel's width, pinned to its bottom edge, rounded only on top.
 * Inset focus ring on a ::before layer so internal dividers cannot cut through it.
 */
export const workspaceAgentConsentDockShell = tailwindVariants({
  base: [
    'relative flex max-h-full min-h-0 w-full flex-1 flex-col overflow-hidden rounded-t-16 ads:bg-base bg-gray-1 shadow-popover outline-none',
    'before:pointer-events-none before:absolute before:inset-0 before:z-10 before:rounded-t-16 before:opacity-0 ads:before:ring-focus before:ring-2 before:ring-altitude-9 before:ring-inset before:content-[""]',
    'focus-visible:before:opacity-100',
    'fade-in slide-in-from-bottom-4 animation-duration-200 animate-in motion-reduce:animate-none',
  ],
});

/** Grid stack cell for composer + consent dock overlay (prototype `ChatStub` relative + `absolute inset-0`). */
export const workspaceAgentComposerDockStackCell = tailwindVariants({
  base: 'col-start-1 row-start-1 min-h-0 w-full min-w-0',
});

/**
 * Composer column with a consent dock open. `max-h-[70%]` caps a tall dock so the thread stays
 * visible. `min-h-0` children let the dock body scroll instead of clipping Cancel / Continue.
 * Overflow stays visible: the sheet is flush with this slot, so `overflow-hidden` masks
 * `shadow-popover` (the 1px edge and the drop shadow) where it meets the thread. The dock shell
 * still clips its own body. Do not put `min-h-full` on the inner grid: percentage min-height
 * resolves against this max-height and stretches a short dock to 70% of the pane.
 */
export const workspaceAgentComposerConsentSlot = tailwindVariants({
  base: 'relative z-1 flex max-h-[70%] min-h-0 shrink-0 flex-col overflow-visible ads:bg-base bg-gray-1',
});

/** Composer column while the input is showing — inset matches the chat body. */
export const workspaceAgentComposerIdleSlot = tailwindVariants({
  base: 'relative z-1 shrink-0 ads:bg-base bg-gray-1 px-3 pb-3',
});

/**
 * Stacks the composer sizer and the dock in one cell. `grow` fills the reserved composer height
 * without a percentage min-height against the slot's 70% cap.
 */
export const workspaceAgentComposerDockGrid = tailwindVariants({
  base: 'grid min-h-0 w-full',
  defaultVariants: {
    isConsentDockOpen: false,
  },
  variants: {
    isConsentDockOpen: {
      false: '',
      true: 'grow',
    },
  },
});

/** Dock overlay cell — `h-full` of the grid, which grows with reserved height or shrinks at the 70% cap. */
export const workspaceAgentComposerDockOverlay = tailwindVariants({
  base: [workspaceAgentComposerDockStackCell(), 'flex h-full min-h-0 flex-col'],
});

/** Prototype `TrinityZeroApprovalDock` title — `text-sm font-semibold leading-5`. */
export const workspaceAgentConsentDockTitle = tailwindVariants({
  base: 'min-w-0 text-pretty break-words font-semibold ads:text-primary text-14 text-gray-12 leading-5',
});

/** Prototype remember row — `text-xs leading-4 text-gray-11` with `gap-1.5`. */
export const workspaceAgentConsentDockRememberLabel = tailwindVariants({
  base: 'flex min-w-0 cursor-pointer items-center gap-1.5 ads:text-secondary text-12 text-gray-11 leading-4',
});

/** Prototype `ComposerTriggerMenu` title — `text-xs font-medium text-gray-10`. */
export const workspaceAgentSkillTriggerMenuTitle = tailwindVariants({
  base: 'font-medium ads:text-metadata text-12 text-gray-10',
});

/** Prototype slash row summary — `text-xs leading-[15px] text-gray-10`. */
export const workspaceAgentSkillTriggerMenuRowSummary = tailwindVariants({
  base: 'truncate ads:text-metadata text-12 text-gray-10 leading-4',
});

/** Prototype empty state — `text-xs text-gray-09`. */
export const workspaceAgentSkillTriggerMenuEmpty = tailwindVariants({
  base: 'ads:text-metadata text-12 text-gray-9',
});

/** Loading / secondary menu copy — same size as row summary, muted body color. */
export const workspaceAgentSkillTriggerMenuSecondary = tailwindVariants({
  base: 'ads:text-metadata text-12 text-gray-10',
});

/**
 * Prototype `SlashCommandChip` — bordered field on the menu surface (`bg-gray-1`), not the gray filled
 * Skills table badge. Uses gray scale only (no `bg-white`) so we do not need per-token `dark:` overrides;
 * the popover chrome matches the mention picker (`bg-gray-1`, no `dark:` on the panel).
 */
export const workspaceAgentSkillSlashCommandChip = tailwindVariants({
  base: 'inline-flex w-fit max-w-full items-center rounded-6 border ads:border-default border-gray-4 ads:bg-base bg-gray-1 px-2 py-0.5 font-medium font-mono ads:text-metadata text-12 text-gray-10',
});

/** Prototype native checkbox — `size-3 rounded-2` with altitude accent. */
export const workspaceAgentConsentDockRememberCheckbox = tailwindVariants({
  base: 'shrink-0 rounded-2 ads:border-strong border-gray-8 radix-state-checked:ads:border-primary radix-state-checked:border-altitude-9 radix-state-checked:ads:bg-primary radix-state-checked:bg-altitude-9 hover:radix-state-checked:ads:bg-hover-primary hover:radix-state-checked:bg-altitude-10',
});

/** Prototype filled altitude approve — `@air/primitive-button` has no altitude color token yet. */
export const workspaceAgentConsentDockApproveButton = tailwindVariants({
  base: 'ads:bg-primary bg-altitude-9 text-white hover:ads:bg-hover-primary hover:bg-altitude-10 hover:text-white active:ads:bg-hover-primary active:bg-altitude-10',
});

/** Keys pressed this soon after the dock opens, or while typing is still going on, are ignored. */
export const WORKSPACE_AGENT_DOCK_KEY_ARM_DELAY_MS = 500;

/** A field with its own Escape to undo first (close Something else) carries this; the dock lets it go first. */
export const WORKSPACE_AGENT_DOCK_LOCAL_ESCAPE_ATTR = 'data-dock-local-escape';

/** Cancel hugs the leading edge, the primary the trailing; the remember bar under it replaces the divider. */
export const workspaceAgentConsentDockFooter = tailwindVariants({
  base: 'flex shrink-0 items-center gap-2 px-4',
  defaultVariants: {
    hasDivider: true,
  },
  variants: {
    hasDivider: {
      false: 'h-16',
      true: 'h-[65px] ads:border-subtle border-gray-3 border-t pt-px',
    },
  },
});

/** Key chip on the outline Cancel button: Escape presses it. */
export const workspaceAgentConsentDockEscapeKey = tailwindVariants({
  base: 'flex h-5 items-center rounded-4 ads:bg-subtle bg-gray-3 px-1 py-px font-medium font-sans ads:text-metadata text-10 text-gray-9 uppercase leading-[1.23] tracking-[0.4px]',
});

/** Key chip on the filled primary: Enter runs it. Shift+Enter in a text field stays a newline. */
export const workspaceAgentConsentDockEnterKey = tailwindVariants({
  base: 'flex h-5 items-center justify-center gap-0.5 rounded-4 bg-white/15 px-1 py-px font-medium font-sans text-10 text-white',
});

/**
 * Stable input-zone height — prototype `COMPOSER_SCOPE_SLOT_PX` (44) + ProseMirror `min-h-10`.
 * Scope overlays the top strip when visible; without scope the editor still fills this slot.
 */
export const workspaceAgentComposerEditorSlot = tailwindVariants({
  base: 'relative flex min-h-[calc(theme(spacing.11)+theme(spacing.10))] flex-col',
  defaultVariants: {
    isCreditsDepleted: false,
  },
  variants: {
    isCreditsDepleted: {
      false: '',
      true: 'w-full items-start justify-start',
    },
  },
});

/**
 * Text offset inside the fixed editor slot — scope overlays `pt-11`; without scope `pt-2` keeps the caret at the top.
 */
export const workspaceAgentComposerScopeSlotPadding = tailwindVariants({
  base: 'flex grow flex-col',
  defaultVariants: {
    hasScope: false,
  },
  variants: {
    hasScope: {
      false: 'pt-2',
      true: 'pt-11',
    },
  },
});

/** Prototype `ComposerScopeBar` — `bg-background-subtle` (gray-3) on shell `bg-gray-2`, no divider. */
export const workspaceAgentComposerScopeBar = tailwindVariants({
  base: 'flex h-8 w-full shrink-0 items-center gap-1.5 ads:bg-subtle bg-gray-3 px-2.5',
});

/** Scope overlays the reserved slot (`ChatStub` `pointer-events-none` wrapper + `pointer-events-auto` bar). */
export const workspaceAgentComposerScopeBarOverlay = tailwindVariants({
  base: 'pointer-events-none absolute inset-x-0 top-0 z-1',
});

export const workspaceAgentComposerScopeBarOverlayInner = tailwindVariants({
  base: [workspaceAgentComposerPointerSurface(), 'w-full min-w-0'],
});

/** Full-width input zone — scope is in-flow only visually; height is reserved via `workspaceAgentComposerScopeSlotPadding`. */
export const workspaceAgentComposerInputRow = tailwindVariants({
  base: 'relative flex w-full flex-col px-3',
});

export const workspaceAgentComposerEditor = tailwindVariants({
  base: 'flex w-full min-w-0 grow flex-col',
});

export const workspaceAgentComposerEditorContent = tailwindVariants({
  base: [
    workspaceAgentComposerPointerSurface(),
    'flex w-full grow flex-col ads:text-primary text-14 text-gray-12 [&>div]:flex [&>div]:grow [&>div]:flex-col [&_.ProseMirror]:box-border [&_.ProseMirror]:min-h-10 [&_.ProseMirror]:w-full [&_.ProseMirror]:grow [&_.ProseMirror]:py-0 [&_.ProseMirror]:leading-5 [&_.ProseMirror]:outline-none [&_.ProseMirror_p]:m-0',
  ],
  variants: {
    isAgentSelectionDropTarget: {
      true: 'pointer-events-none [&_.ProseMirror]:caret-transparent',
    },
  },
});

/** Composer footer — prototype `ComposerAgentFooter` chrome (`px-2 pb-2`); Actions leads, send/stop trails. */
export const workspaceAgentComposerActionRow = tailwindVariants({
  base: 'flex w-full items-stretch justify-between px-2 pt-2 pb-2',
});

/** Mention / send controls — pointer surface; gap between groups hits the focus capture layer. */
export const workspaceAgentComposerActionRowGroup = tailwindVariants({
  base: [workspaceAgentComposerPointerSurface(), 'flex min-w-0 shrink-0 items-center gap-2'],
});

/**
 * Its own square at the chip's trailing edge, flush to it — the chip's `overflow-hidden` rounds it.
 * That same `overflow-hidden` would clip an outset ring, so focus is drawn inset; the `forced-colors`
 * outline stands in where a forced-colors palette drops the ring's background.
 */
export const workspaceAgentComposerPillRemoveButton = tailwindVariants({
  base: 'inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-none ads:text-icon-default text-gray-10 hover:ads:bg-hover-subtle hover:bg-gray-5 hover:ads:text-icon-emphasis hover:text-gray-12 focus-visible:outline-none focus-visible:ads:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-9 focus-visible:ring-inset forced-colors:focus-visible:outline forced-colors:focus-visible:outline-2',
});

/**
 * Only `aria-disabled` (like send), so the muted state is restated here; the primitive keys off
 * `disabled:`. Muted rather than send's ground: an unavailable Actions button should read as out of
 * reach, not as a filled button waiting to be pressed.
 */
export const workspaceAgentComposerActionsButton = tailwindVariants({
  base: 'transition-none aria-disabled:cursor-not-allowed ads:aria-disabled:bg-raised aria-disabled:bg-gray-2 ads:aria-disabled:text-icon-subtle aria-disabled:text-gray-9 ads:aria-disabled:active:bg-raised aria-disabled:active:bg-gray-2 ads:aria-disabled:active:text-icon-subtle aria-disabled:active:text-gray-9 ads:aria-disabled:hover:bg-raised aria-disabled:hover:bg-gray-2 ads:aria-disabled:hover:text-icon-subtle aria-disabled:hover:text-gray-9',
});

export const workspaceAgentComposerSendButton = tailwindVariants({
  base: 'transition-none aria-disabled:cursor-not-allowed ads:aria-disabled:bg-subtle aria-disabled:bg-gray-3 aria-disabled:ads:text-icon-emphasis aria-disabled:text-gray-12 ads:aria-disabled:active:bg-subtle aria-disabled:active:bg-gray-3 aria-disabled:active:ads:text-icon-emphasis aria-disabled:active:text-gray-12 ads:aria-disabled:hover:bg-subtle aria-disabled:hover:bg-gray-3 aria-disabled:hover:ads:text-icon-emphasis aria-disabled:hover:text-gray-12',
});

export const workspaceAgentComposerStopButton = tailwindVariants({
  base: 'transition-none',
  defaultVariants: {
    isInteractionDisabled: false,
  },
  variants: {
    isInteractionDisabled: {
      false: '',
      true: 'cursor-not-allowed ads:bg-subtle bg-gray-3 ads:text-primary text-gray-12 ads:hover:bg-subtle hover:bg-gray-3',
    },
  },
});
